import React, { createContext, useContext, useMemo } from 'react';

import type { Actor } from '@avatar/types';
import {
  ProjectAccessRoleEnum,
  StudioProjectVersion,
} from '../../../../server/graphql/__generated.types';

interface StudioProjectContextProps {
  projectId: string;
  projectName: string;
  studioVersion: StudioProjectVersion;
  // note: to be deprecated in the future in favor of using the avatar context
  projectAvatars: Actor[];
  sampleRate: number;
  projectAccess?: ProjectAccessRoleEnum;
  defaultPauseLength: number | null;
  driveId: string;
  projectVoices?: number[];
}

export const ALLOWED_SAMPLE_RATES = [24000, 48000, 96000] as const;
export type SampleRateHz = (typeof ALLOWED_SAMPLE_RATES)[number];

export enum SampleRate {
  Hz24000 = 24000,
  Hz48000 = 48000,
  Hz96000 = 96000,
}
export const DEFAULT_SAMPLE_RATE = SampleRate.Hz24000;

const StudioProjectContext = createContext<StudioProjectContextProps>({
  projectId: '',
  projectName: '',
  studioVersion: StudioProjectVersion.ScriptStudio,
  projectAvatars: [],
  projectAccess: undefined,
  sampleRate: DEFAULT_SAMPLE_RATE,
  defaultPauseLength: null,
  driveId: '',
  projectVoices: [],
});

export const useStudioProjectContext = () => {
  return useContext(StudioProjectContext);
};

export default function StudioProjectProvider({
  children,
  projectId,
  projectName,
  studioVersion,
  projectAvatars,
  projectAccess,
  sampleRate,
  defaultPauseLength,
  driveId = '',
  projectVoices = [],
}: {
  children: React.ReactNode;
  projectId: string;
  projectName: string;
  studioVersion: StudioProjectVersion;
  projectAvatars: Actor[];
  sampleRate: number;
  projectAccess?: ProjectAccessRoleEnum;
  defaultPauseLength: number | null;
  driveId?: string;
  projectVoices?: number[];
}) {
  const value = useMemo(
    () => ({
      projectId,
      projectName,
      studioVersion,
      projectAvatars,
      projectAccess,
      sampleRate,
      defaultPauseLength,
      driveId,
      projectVoices,
    }),
    [
      projectId,
      projectName,
      studioVersion,
      projectAvatars,
      projectAccess,
      sampleRate,
      defaultPauseLength,
      driveId,
      projectVoices,
    ]
  );

  return (
    <StudioProjectContext.Provider value={value}>
      {children}
    </StudioProjectContext.Provider>
  );
}
